import dashboardActions from '../../../actions/dashboard.actions';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {getLocale} from '@epic-core/common';
import {BrowseSort} from '../types';
import {useDelayCallback} from '../../../hooks';
import {DashboardProducts, ProductsQueryState} from '../../../reducers/dashboard';
import {parseLocationQuery, updateBrowserHistory} from '../../../utils/PageUtils';
import {useHistory} from 'react-router-dom';

const useBrowseProducts = (productsState: DashboardProducts) => {
    const dispatch = useDispatch();
    const delay = useDelayCallback();
    const history = useHistory();
    const [state, setState] = useState(() => ({
        mounted: false,
        loading: false,
        clearCache: false
    }));

    const queryState = productsState.queryState;
    const {data: games} = productsState || {};
    const cachedProducts = games ? games[queryState.page] || [] : [];

    const extractQueryStateFromLocation = () => {
        // `keywords` not parsing in epic-tracking, try `kws` instead
        const q = parseLocationQuery<ProductsQueryState & {kws: string}>(history.location);
        return {
            keywords: q.keywords || q.kws || queryState.keywords,
            locale: getLocale(), // applied later
            page: q.page || queryState.page,
            start: q.start || queryState.start,
            count: q.count || queryState.count,
            sortBy: q.sortBy || queryState.sortBy,
            sortDir: q.sortDir || queryState.sortDir
        };
    };

    const updateLocationFromQueryState = () => {
        // `keywords` not parsing, use `kws` instead
        const {keywords: kws, locale, ...rest} = queryState;
        updateBrowserHistory(history, {kws, ...rest});
    };

    const updateQueryState = (payload: Partial<ProductsQueryState>, clearCache = false) => {
        if (state.clearCache !== clearCache) {
            setState({...state, clearCache});
        }
        const {page = queryState.page, count = queryState.count} = payload || {};
        const newQueryState = {
            ...payload,
            locale: getLocale(),
            start: page >= 1 ? count * page : 0
        };
        dispatch(dashboardActions.updateProductQueryState(newQueryState));
        return newQueryState;
    };

    const getProducts = (payload?: Partial<ProductsQueryState>) => {
        setState({...state, loading: true});
        delay.add(() => {
            const data = {...queryState, ...payload, version: 2};
            dispatch(dashboardActions.getProducts(data));
            updateLocationFromQueryState();
            setState({...state, loading: false});
        }, 500);
    };

    useEffect(() => {
        if (!state.mounted) {
            const qs = extractQueryStateFromLocation();
            updateQueryState(qs);
            getProducts(qs);
            setState({...state, mounted: true});
            return;
        }

        if (state.clearCache) {
            dispatch(dashboardActions.resetProducts());
        }

        getProducts();
    }, [queryState.sortBy, queryState.sortDir, queryState.keywords, queryState.count]);

    return {
        loading: state.loading,
        products: cachedProducts,
        onSortChange: (browseSort: BrowseSort) => updateQueryState({page: 0, ...browseSort}, true),
        onKeywordsChange: (keywords: string) => updateQueryState({page: 0, keywords}, true),
        onPostsPerPageChange: (count: number) => updateQueryState({page: 0, count}, true),
        onPageChange: (page: number) => {
            const qs = updateQueryState({page});
            const isCached = games[page] && games[page].length > 0;
            if (queryState.page !== page && !isCached) {
                getProducts(qs);
            }
        }
    };
};

export default useBrowseProducts;
